import * as Yup from 'yup';
import {
  Origins,
  ScopeThreeSection,
} from 'models/ScopeThreeCategoryOne/ScopeThreeCategoryOne.model';
import { Category1TypeFields } from 'enums/category1Enums';
import { maxFourDecimalsTest } from 'views/ActivityDataEntry/ScopeOneViewComponent/validationSchema';

interface TestContextExtended {
  from: {
    value: ScopeThreeSection;
  }[];
}

export const validatePercentages = Yup.number().transform(
  (value, originalValue) => {
    // If the original value is not a valid number, return null
    return isNaN(value) && originalValue === '.' ? null : value;
  }
);

// Custom validation function to check if any of the fields are filled, others must be required
const fieldRequiredIfAnyOtherFieldFilled: Yup.TestFunction = function (
  this,
  value
) {
  const { activityLabel, quantity, unit, bestFitRegion, origins } = this.parent;
  // Check if any of the fields are filled
  const isAnyFieldFilled = activityLabel || quantity || unit || bestFitRegion;

  // Checking any origin's related field is filled
  const hasOriginFields = origins?.some(
    ({ share, addressLabel, activityMappingRegionSpecId }: Origins) =>
      share != null ||
      addressLabel != null ||
      activityMappingRegionSpecId != null
  );

  if (hasOriginFields || isAnyFieldFilled) {
    // If any field is filled, this field must also be filled
    return value !== undefined && value !== null && value !== '';
  }
  return true; // If no field is filled, this field is not required
};

// Custom validation function to check if both activityMappingRegionSpecId and address.country are required
// when one of them is filled
const fieldRequiredIfOneOriginFieldFilled: Yup.TestFunction = function (
  this,
  value
) {
  const { activityLabel, quantity, unit, bestFitRegion, origins } = (
    this as Yup.TestContext & TestContextExtended
  ).from?.[1]?.value;

  const isAnyFieldFilled = activityLabel || quantity || unit || bestFitRegion;

  if (isAnyFieldFilled) {
    const isShare = this?.path.includes('share');
    if (isShare && origins?.length < 2) return true;
    return value !== undefined && value !== null && value !== '';
  }

  return true;
};

const createValidationForCategory = (category: keyof ScopeThreeSection) => {
  return Yup.array().of(
    Yup.object().shape({
      activityLabel: Yup.string()
        .nullable()
        .test(
          'requiredIfAnyFieldFilled',
          'Field is required',
          fieldRequiredIfAnyOtherFieldFilled
        )
        .test(
          'unique-activityLabel',
          'Duplicate item selected',
          function (this) {
            const array = (this as Yup.TestContext & TestContextExtended)
              .from[1].value[category] as ScopeThreeSection[]; // Reference to the array of objects for the current category
            const currentItem = (this as Yup.TestContext & TestContextExtended)
              .from[0].value;
            const labels = array
              .filter(item => item.activityLabel !== null)
              .map(item => item.activityLabel) as string[];
            const count = labels.filter(
              label => label === currentItem.activityLabel
            ).length;
            return count <= 1;
          }
        ),
      quantity: maxFourDecimalsTest
        .min(0, 'Quantity must be greater than or equal to 0')
        .test(
          'requiredIfAnyFieldFilled',
          'Field is required',
          fieldRequiredIfAnyOtherFieldFilled
        ),
      unit: Yup.string()
        .nullable()
        .test(
          'requiredIfAnyFieldFilled',
          'Field is required',
          fieldRequiredIfAnyOtherFieldFilled
        ),
      bestFitRegion: Yup.string().nullable(),
      origins: Yup.array().of(
        Yup.object().shape({
          activityMappingRegionSpecId: Yup.string()
            .nullable()
            .test(
              'requiredIfOneOriginFieldFilled',
              'Field is required',
              fieldRequiredIfOneOriginFieldFilled
            ),
          addressLabel: Yup.string()
            .nullable()
            .test(
              'requiredIfOneOriginFieldFilled',
              'Field is required',
              fieldRequiredIfOneOriginFieldFilled
            ),
          share: validatePercentages
            .min(0, 'Share must be greater than or equal to 0')
            .max(100, 'Share must be lesser than or equal to 100')
            .nullable()
            .test(
              'requiredIfOneOriginFieldFilled',
              'Enter a valid share',
              fieldRequiredIfOneOriginFieldFilled
            ),
        })
      ),
    })
  );
};

export const validationSchema = Yup.object().shape({
  ingredients: createValidationForCategory(Category1TypeFields.INGREDIENTS),
  packagings: createValidationForCategory(
    Category1TypeFields.PACKAGING_MATERIALS
  ),
  others: createValidationForCategory(Category1TypeFields.OTHERS),
});
